<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_assets_new') }}
      </h4>

      <div class="d-flex mb-1">
        <b-button
          class="mr-50 rounded-pill"
          :variant="form.acquisition_method === 'opening' ? 'primary' : 'outline-primary'"
          @click="form.acquisition_method = 'opening'"
        >
          {{ $t('ledger_assets_method_opening') }}
        </b-button>
        <b-button
          class="rounded-pill"
          :variant="form.acquisition_method === 'purchase' ? 'primary' : 'outline-primary'"
          @click="form.acquisition_method = 'purchase'"
        >
          {{ $t('ledger_assets_method_purchase') }}
        </b-button>
      </div>

      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_code')">
            <b-form-input
              v-model="form.asset_code"
              :placeholder="$t('ledger_assets_code_auto')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_name')">
            <b-form-input v-model="form.name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_name_ar')">
            <b-form-input v-model="form.name_ar" />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_category')">
            <b-form-select
              v-model="form.category"
              :options="categoryOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_acquired')">
            <b-form-input
              v-model="form.acquisition_date"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_posting_date')">
            <b-form-input
              v-model="form.posting_date"
              type="date"
              @input="postingTouched = true"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_cost')">
            <b-form-input
              v-model.number="form.cost"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="depreciable"
          cols="6"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_salvage')">
            <b-form-input
              v-model.number="form.salvage_value"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="depreciable"
          cols="6"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_life')">
            <b-form-input
              v-model.number="form.useful_life_months"
              type="number"
              min="1"
              step="1"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="depreciable"
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_start')">
            <b-form-input
              v-model="form.depreciation_start_date"
              type="date"
              @input="startTouched = true"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="depreciable && form.acquisition_method === 'opening'"
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_opening_accum')">
            <b-form-input
              v-model.number="form.opening_accumulated_depreciation"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="form.acquisition_method === 'purchase'"
          cols="12"
          md="6"
        >
          <b-form-group :label="$t('ledger_payroll_till')">
            <v-select
              v-model="form.account_id"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="tills"
              :reduce="option => option.id"
              :get-option-label="tillLabel"
              :loading="loadingTills"
              :placeholder="$t('ledger_payroll_till')"
              class="ledger-account-select"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_serial')">
            <b-form-input v-model="form.serial_number" />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('ledger_assets_location')">
            <b-form-input v-model="form.location" />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group :label="$t('ledger_assets_notes')">
            <b-form-textarea
              v-model="form.notes"
              rows="2"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <p
        v-if="depreciable && monthlyHint != null"
        class="text-muted"
      >
        {{ $t('ledger_assets_monthly_hint') }}
        <strong class="ledger-num">{{ money(monthlyHint) }}</strong>
      </p>
      <p
        v-if="isLand"
        class="text-muted"
      >
        {{ $t('ledger_assets_land_note') }}
      </p>

      <b-alert
        v-if="showLinksHint"
        show
        variant="warning"
      >
        <b-link :to="{ name: 'ledger-links' }">
          {{ $t('ledger_links_title') }}
        </b-link>
      </b-alert>

      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSubmit"
        @click="confirmCreate"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-50"
        />
        {{ $t('ledger_assets_register_btn') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BAlert, BLink, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { createFixedAsset } from '@/api/accounting/fixedAssets'
import { fetchAllOperationalAccounts } from '@/api/accounting/accounts'
import {
  oneFrom, enumValue, formatLedgerMoney, roundMoney, todayIso, apiErrorMessage,
} from '@/utils/ledger'

const CATEGORIES = ['land', 'buildings', 'survey_equipment', 'computers', 'furniture', 'vehicles']

function firstOfNextMonth(iso) {
  if (!iso) return ''
  const [year, month] = iso.split('-').map(Number)
  const date = new Date(year, month, 1)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default {
  name: 'FixedAssetForm',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BAlert,
    BLink,
    BSpinner,
    vSelect,
  },
  data() {
    const today = todayIso()
    return {
      saving: false,
      loadingTills: false,
      tills: [],
      showLinksHint: false,
      startTouched: false,
      postingTouched: false,
      form: {
        asset_code: '',
        name: '',
        name_ar: '',
        category: 'survey_equipment',
        acquisition_method: 'opening',
        acquisition_date: today,
        posting_date: today,
        cost: null,
        salvage_value: 0,
        useful_life_months: null,
        depreciation_start_date: firstOfNextMonth(today),
        opening_accumulated_depreciation: 0,
        account_id: null,
        serial_number: '',
        location: '',
        notes: '',
      },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    isLand() {
      return this.form.category === 'land'
    },
    depreciable() {
      return !this.isLand
    },
    categoryOptions() {
      return CATEGORIES.map(value => ({ value, text: this.$t(`ledger_assets_cat_${value}`) }))
    },
    monthlyHint() {
      const cost = Number(this.form.cost)
      const salvage = Number(this.form.salvage_value) || 0
      const life = Number(this.form.useful_life_months)
      if (!Number.isFinite(cost) || !Number.isFinite(life) || life <= 0 || cost <= salvage) return null
      return roundMoney((cost - salvage) / life)
    },
    canSubmit() {
      if (!String(this.form.name || '').trim()) return false
      if (!this.form.acquisition_date) return false
      if (!Number.isFinite(Number(this.form.cost)) || Number(this.form.cost) <= 0) return false
      if (this.depreciable) {
        const salvage = Number(this.form.salvage_value) || 0
        if (salvage >= Number(this.form.cost)) return false
        if (!Number.isFinite(Number(this.form.useful_life_months)) || Number(this.form.useful_life_months) < 1) return false
      }
      if (this.form.acquisition_method === 'purchase' && !this.form.account_id) return false
      return true
    },
  },
  watch: {
    'form.acquisition_date': function onAcquired(value) {
      if (!this.startTouched) this.form.depreciation_start_date = firstOfNextMonth(value)
      if (!this.postingTouched && this.form.acquisition_method === 'purchase') this.form.posting_date = value
    },
    'form.acquisition_method': function onMethod(value) {
      if (!this.postingTouched) {
        this.form.posting_date = value === 'purchase' ? this.form.acquisition_date : todayIso()
      }
      if (value === 'purchase') this.loadTills()
    },
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    tillLabel(account) {
      if (!account) return ''
      const code = account.code ? `${account.code} — ` : ''
      return `${code}${account.name || ''}`
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async loadTills() {
      if (this.tills.length || this.loadingTills) return
      this.loadingTills = true
      try {
        const rows = await fetchAllOperationalAccounts({ status: 'active', per_page: 100 })
        this.tills = rows.filter(account => {
          const type = enumValue(account.type)
          return type === 'cash' || type === 'bank' || type === 'petty_cash'
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingTills = false
      }
    },
    journalLines() {
      const cost = this.money(this.form.cost)
      if (this.form.acquisition_method === 'purchase') {
        return [
          this.$t('ledger_assets_line_dr_cost', { amount: cost }),
          this.$t('ledger_assets_line_cr_till', { amount: cost }),
        ]
      }
      const accum = roundMoney(this.depreciable ? (this.form.opening_accumulated_depreciation || 0) : 0)
      const lines = [this.$t('ledger_assets_line_dr_cost', { amount: cost })]
      if (accum > 0) lines.push(this.$t('ledger_assets_line_cr_accum', { amount: this.money(accum) }))
      lines.push(this.$t('ledger_assets_line_cr_equity', { amount: this.money(roundMoney(Number(this.form.cost) - accum)) }))
      return lines
    },
    payload() {
      const body = {
        name: String(this.form.name).trim(),
        category: this.form.category,
        acquisition_method: this.form.acquisition_method,
        acquisition_date: this.form.acquisition_date,
        cost: roundMoney(this.form.cost),
      }
      const nameAr = String(this.form.name_ar || '').trim()
      const code = String(this.form.asset_code || '').trim()
      const serial = String(this.form.serial_number || '').trim()
      const location = String(this.form.location || '').trim()
      const notes = String(this.form.notes || '').trim()
      if (nameAr) body.name_ar = nameAr
      if (code) body.asset_code = code
      if (serial) body.serial_number = serial
      if (location) body.location = location
      if (notes) body.notes = notes
      if (this.form.posting_date) body.posting_date = this.form.posting_date
      if (this.depreciable) {
        body.salvage_value = roundMoney(this.form.salvage_value || 0)
        body.useful_life_months = Number(this.form.useful_life_months)
        if (this.form.depreciation_start_date) body.depreciation_start_date = this.form.depreciation_start_date
      }
      if (this.form.acquisition_method === 'opening' && this.depreciable) {
        body.opening_accumulated_depreciation = roundMoney(this.form.opening_accumulated_depreciation || 0)
      }
      if (this.form.acquisition_method === 'purchase') body.account_id = this.form.account_id
      return body
    },
    async confirmCreate() {
      if (!this.canSubmit || this.saving) return
      const result = await Swal.fire({
        title: this.$t('ledger_assets_confirm_title'),
        html: this.journalLines().map(line => `<div>${line}</div>`).join(''),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_assets_register_btn'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.saving = true
      this.showLinksHint = false
      try {
        const res = await createFixedAsset(this.payload())
        const asset = oneFrom(res)
        this.toast(this.$t('ledger_assets_created'), 'success')
        const journalId = asset && (asset.acquisition_journal_entry_id
          || (asset.acquisition_journal_entry && asset.acquisition_journal_entry.id))
        if (journalId && this.hasPermission('view journal_entries')) {
          this.$router.push({ name: 'ledger-journals-view', params: { id: journalId } })
          return
        }
        if (asset && asset.id) {
          this.$router.push({ name: 'ledger-assets-view', params: { id: asset.id } })
        }
      } catch (error) {
        const message = apiErrorMessage(error, this.$t('ledger_error'))
        this.toast(message, error?.response?.status === 422 ? 'warning' : 'danger')
        const text = String(message || '').toLowerCase()
        this.showLinksHint = (text.includes('ربط') || text.includes('unmapped') || text.includes('link'))
          && this.hasPermission('edit gl_links')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-num {
  font-variant-numeric: tabular-nums;
}
</style>
